/**
 * Pure payload builders for the GTM tagging plan (BR-57983). No DOM access —
 * everything here is unit-testable. Transport lives in gtm.ts.
 */
import type { BookedAncillaryDTO, BookingDTO } from '@/types/dto/agent';

export type GTMSiteEnvironment = 'dev' | 'staging' | 'preprod' | 'prod';

const ENVIRONMENT_NAME_MAP: Record<string, GTMSiteEnvironment> = {
  development: 'dev',
  dev: 'dev',
  staging: 'staging',
  prelive: 'preprod',
  preprod: 'preprod',
  production: 'prod',
  prod: 'prod',
};

export const mapEnvironmentName = (
  name: string | undefined
): GTMSiteEnvironment | undefined =>
  name ? ENVIRONMENT_NAME_MAP[name.toLowerCase()] : undefined;

export const getSiteEnvironment = (): GTMSiteEnvironment | undefined =>
  mapEnvironmentName(import.meta.env.VITE_APP_ENVIRONMENT);

const PAGE_TYPE_RULES: Array<[RegExp, string]> = [
  [/^\/purchase\/outbound\/journey/, 'Outbound journeys search results'],
  [/^\/purchase\/inbound\/journey/, 'Inbound journeys search results'],
  [/^\/purchase\/(outbound|inbound|non-trip)\/offer/, 'Offer selection'],
  [/^\/purchase\/checkout/, 'Checkout'],
  [/^\/purchase/, 'Search'],
  [/^\/success/, 'Order confirmation'],
  [/^\/login/, 'Login'],
  [/^\/signup/, 'Sign-up'],
  [/^\/reset-password/, 'Reset password'],
  [/^\/manage-booking/, 'Manage booking'],
  [/^\/journey-change/, 'Journey change'],
  [/^\/refund/, 'Refund'],
  [/^\/account/, 'Account'],
];

export const getPageType = (pathname: string): string =>
  PAGE_TYPE_RULES.find(([pattern]) => pattern.test(pathname))?.[1] ?? 'Other';

export interface GTMTripItem {
  origin?: string;
  destination?: string;
  start_date?: string;
  departure_time?: string;
  end_date?: string;
  arrival_time?: string;
  quantity: number;
  price?: number;
}

export interface GTMExtrasItem {
  item_name?: string;
  item_id?: string;
  price?: number;
  quantity: number;
}

// Covers both SearchJourneyOffersDTO (search results) and BookedTripDTO
// (booking) — they share this structural subset.
interface GTMJourneyLike {
  departureTime?: string;
  arrivalTime?: string;
  originStop?: { name?: string } | null;
  destinationStop?: { name?: string } | null;
  lowestPrice?: { amount?: number; currency?: string } | null;
}

// Summed float prices accumulate binary-representation noise
// (30 + 37.02 = 67.02000000000001); GA4 should receive clean amounts.
export const roundMoney = (amount: number): number =>
  Math.round(amount * 100) / 100;

// The tagging plan wants dates/times as displayed to the user. The API sends
// ISO strings with the local offset, so slice the literal fields instead of
// round-tripping through Date (which would shift to the browser timezone).
export const formatGTMDate = (isoDateTime: string | undefined) =>
  isoDateTime ? isoDateTime.slice(0, 10) : undefined;

export const formatGTMTime = (isoDateTime: string | undefined) =>
  isoDateTime ? isoDateTime.slice(11, 16) : undefined;

export const mapJourneyToGTMItem = (
  journey: GTMJourneyLike,
  quantity: number,
  price?: number
): GTMTripItem => ({
  origin: journey.originStop?.name,
  destination: journey.destinationStop?.name,
  start_date: formatGTMDate(journey.departureTime),
  departure_time: formatGTMTime(journey.departureTime),
  end_date: formatGTMDate(journey.arrivalTime),
  arrival_time: formatGTMTime(journey.arrivalTime),
  quantity,
  price: price ?? journey.lowestPrice?.amount,
});

// One item per booked trip: quantity = passenger count, price = sum of the
// trip's admission prices (the item is the ticket line, not one admission).
export const mapBookingToGTMTripItems = (
  booking: BookingDTO | null | undefined
): GTMTripItem[] =>
  booking?.bookedTrips?.map((trip) => {
    const admissionsTotal = (trip.bookedOffers ?? [])
      .flatMap((offer) => offer.admissions ?? [])
      .reduce((sum, admission) => sum + (admission.price?.amount ?? 0), 0);

    return mapJourneyToGTMItem(
      trip,
      booking.passengers?.length ?? 1,
      roundMoney(admissionsTotal)
    );
  }) ?? [];

export const mapBookedAncillaryToExtrasItem = (
  ancillary: BookedAncillaryDTO
): GTMExtrasItem => ({
  item_id: ancillary.id,
  item_name: ancillary.productDescription ?? ancillary.productSummary,
  price: ancillary.price?.amount,
  quantity: ancillary.amountOf ?? ancillary.passengerIds?.length ?? 1,
});

// Deliberately not reusing utils/booking's getBookingAdmissions — this module
// stays import-light so it can be unit-tested without the app's full
// dependency graph.
const getBookingOfferAdmissions = (booking: BookingDTO | null | undefined) => {
  const offers = booking?.bookedTrips?.length
    ? booking.bookedTrips.flatMap((trip) => trip.bookedOffers ?? [])
    : (booking?.nonTripOffers ?? []);

  return offers.flatMap((offer) => offer.admissions ?? []);
};

export const getBookingAncillaries = (
  booking: BookingDTO | null | undefined
): BookedAncillaryDTO[] =>
  getBookingOfferAdmissions(booking).flatMap(
    (admission) => admission.ancillaries ?? []
  );

// A code can be applied to multiple admissions but should be reported once.
export const getBookingPromoCodes = (
  booking: BookingDTO | null | undefined
): string[] => [
  ...new Set(
    getBookingOfferAdmissions(booking)
      .flatMap((admission) => admission.appliedPromotionCodes ?? [])
      .map(({ code }) => code)
      .filter((code): code is string => Boolean(code))
  ),
];

export const getBookingVoucherCodes = (
  booking: BookingDTO | null | undefined
): string[] =>
  (booking?.pendingVouchers ?? [])
    .map(({ voucher_code }) => voucher_code)
    .filter(Boolean);

/**
 * Tagging plan (BR-57983): the payment rail is suffixed with the discount
 * mechanisms used on the booking, e.g. "Adyen | promo_code" or
 * "Klix | voucher" (both when both apply). The voucher suffix is skipped when
 * the rail itself is the voucher payment.
 */
export const buildPaymentType = (
  rail: string,
  booking: BookingDTO | null | undefined
): string =>
  [
    rail,
    ...(getBookingPromoCodes(booking).length ? ['promo_code'] : []),
    ...(getBookingVoucherCodes(booking).length && rail !== 'Voucher'
      ? ['voucher']
      : []),
  ].join(' | ');
